import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Lock, UserCircle, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HRLoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [employeeForm, setEmployeeForm] = useState({ email: '', password: '' });

  const handleEmployeeLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/employee/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employeeForm)
      });
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('employeeToken', data.token);
        localStorage.setItem('employeeId', data.employee_id);
        localStorage.setItem('userRole', 'employee');
        navigate('/employee/portal');
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      setError('Error connecting to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Back to Home Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Website</span>
        </Link>

        <Card className="shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <UserCircle className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Employee Login</CardTitle>
            <CardDescription>STEAM-IE Employee Portal</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleEmployeeLogin} className="space-y-4" data-testid="employee-login-form">
              <div>
                <Label>Email Address</Label>
                <Input 
                  type="email"
                  value={employeeForm.email} 
                  onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})} 
                  required 
                  placeholder="Enter your email"
                  data-testid="employee-email"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input 
                  type="password" 
                  value={employeeForm.password} 
                  onChange={(e) => setEmployeeForm({...employeeForm, password: e.target.value})} 
                  required 
                  placeholder="Enter password"
                  data-testid="employee-password"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading} data-testid="employee-login-btn">
                {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                Login
              </Button>
              <p className="text-center text-sm text-gray-500 mt-4">
                Check your email for login credentials after accepting the offer letter.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © {new Date().getFullYear()} STEAM-IE. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default HRLoginPage;
